import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  camelCaseToTitleCase,
  formatLocalizedDateTime,
  Logger,
  useLocalize,
} from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  InlineMessage,
  InputField,
  P,
  TabContent,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
  useTooltipText,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  Entitlement,
  ModuleDetails,
  RoleDetails,
  Session,
  SessionDetails,
} from '../../../../../../models/PamModels';
import { isRdp } from '../../../../../../utilities';
import { generateSshTokenApi } from '../../../../../../utilities/api/getDataFromApi';
import CountdownTimer from '../../../../../CountdownTimer/CountdownTimer';
import { msgs } from '../../../../constants';
import SpvEndpoint from '../../../Endpoints/SpvEndpoint/SpvEndpoint';
import SpvEntitlements from '../../../Roles/SpvEntitlements/SpvEntitlements';
import SpvRole from '../../../Roles/SpvRole/SpvRole';
import SpvSection from '../../../SpvSection/SpvSection';

import './SpvActiveSession.css';

const CREDENTIALED_KEYS = {
  PASSWORD: 'password',
  PRIVATE_KEY: 'privateKey',
  SECRET: 'secret',
};

/**
 * To render copy button with tooltip functionality
 * When user clicks on copy button
 * value gets copied button colour will change to green
 * after 5 second, It will come to default state
 */

function CopyButtonWithTooltip({
  handleCopyOnClick,
  isCopyButtonClicked,
  copyButtonKey,
  copyButtonValue,
  isCopyButtonDisabled,
}) {
  const localize = useLocalize();
  const [isSshTokenCopied, setIsSshTokenCopied] = useState(false);
  const {
    tooltipText,
    isDisabled,
    handleTooltipTriggerClick,
    handleTooltipClose,
    tooltipCloseDelayAfterClick,
  } = useTooltipText({
    initialText: localize(msgs.pam.common.copy),
    updatedText: localize(msgs.pam.common.copied),
    closeDelayAfterClick: 5000,
  });

  const isSuccess = !isDisabled && (isCopyButtonClicked || isSshTokenCopied);

  const handleOnClick = async () => {
    if (copyButtonKey === 'sshToken') {
      setIsSshTokenCopied(true);
    }

    await handleCopyOnClick(copyButtonKey, copyButtonValue);
    handleTooltipTriggerClick();
  };

  return (
    <Tooltip
      text={
        isSshTokenCopied && !isDisabled
          ? localize(msgs.pam.common.copied)
          : tooltipText
      }
      placement='top'
      closeDelayAfterClick={tooltipCloseDelayAfterClick}
      onClose={handleTooltipClose}
      isDisabled={isSuccess || isCopyButtonDisabled}
      trigger={
        <Button
          type='button'
          kind='outlined'
          size='large'
          isSuccess={isSuccess}
          isDisabled={isCopyButtonDisabled}
          leftIcon={
            <Icon
              kind={isSuccess ? 'check' : 'Copy'}
              color='neutral-100'
              size='medium'
            />
          }
          onClick={handleOnClick}
          dataTestId={`copy-button-${copyButtonKey}`}
        />
      }
    />
  );
}

function SpvActiveSession({
  userAccessData,
  accountDetails,
  endpointDetails,
  session,
  sessionDetails,
  roleDetails,
  entitlements,
  isMobileWidth,
  dataTestId,
  className,
  isJitForApps,
}) {
  const classes = classnames('SpvActiveSession', className);

  const [activeTab, setActiveTab] = useState('1');
  const [showPassword, setShowPassword] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [clickedCopyButton, setClickedCopyButton] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [sshToken, setSshToken] = useState();
  const [sshTokenExpiry, setSshTokenExpiry] = useState();
  const [isSshTokenVisible, setIsSshTokenVisible] = useState(false);

  const isCredentialless = session?.pamType?.toUpperCase() === 'CREDENTIALLESS';
  const { isNativeSshEnabled = '0', isSshTokenGenerationEnabled = '0' } =
    useSelector((state) => state.user?.nativeSSHconfig) || {};

  const isTokenGenerationEnabled =
    isNativeSshEnabled === '1' && isSshTokenGenerationEnabled === '1';

  const isAdServer =
    isRdp(userAccessData?.remoteAppMetadata) &&
    session?.pamPlatformType === 'ADConsole';

  const isUnix = session?.pamPlatformType === 'UNIX';

  const localize = useLocalize();

  const handleChange = (value) => {
    setActiveTab(value);
  };

  const hasSessionTimeFields =
    session?.sessionStartDate || session?.sessionEndDate;

  // TODO: update to not use mock tcodes when applicable
  const tCodes = session?.codes?.split(',');
  const sapTransactionCodesHasFields = tCodes?.length > 0;

  const hasTargetSystemFields =
    endpointDetails?.customproperty9 || endpointDetails?.customproperty35;

  const remoteAppName = userAccessData?.remoteAppMetadata?.displayName;

  const showRemoteAppName =
    isCredentialless && remoteAppName && remoteAppName !== 'Terminal';

  const isPasswordEnabledCredentialless =
    session?.accountConfig?.['Saviynt-Status']?.showPassword === 'ENABLED';

  const isCredentiallessWithPasswordEnabled =
    isCredentialless && isPasswordEnabledCredentialless;

  const hasSessionAccountFields =
    session?.accountName ||
    accountDetails?.description ||
    userAccessData?.accountDescription ||
    accountDetails?.platformType ||
    accountDetails?.pamAccountType ||
    accountDetails?.accountConfig?.authenticationType;

  const hasRole = userAccessData?.roleKey && roleDetails;
  const hasEntitlements = userAccessData?.roleKey && entitlements?.length > 0;

  const credentiallessSectionHasDivider =
    hasSessionTimeFields || sapTransactionCodesHasFields;
  const remoteAppSectionHasDivider =
    credentiallessSectionHasDivider || hasTargetSystemFields;
  const accountSectionHasDivider =
    remoteAppSectionHasDivider || showRemoteAppName;

  const handleCopyOnClick = async (key, value) => {
    setClickedCopyButton(key);

    await navigator.clipboard.writeText(value);
  };

  const handleMaskedFields = (key) => {
    switch (key) {
      case CREDENTIALED_KEYS.PASSWORD:
        setShowPassword(!showPassword);
        break;
      case CREDENTIALED_KEYS.PRIVATE_KEY:
        setShowPrivateKey(!showPrivateKey);
        break;
      case CREDENTIALED_KEYS.SECRET:
        setShowSecret(!showSecret);
        break;
      default:
        break;
    }
  };

  const showToggleState = (key) => {
    switch (key) {
      case CREDENTIALED_KEYS.PASSWORD:
        return showPassword;
      case CREDENTIALED_KEYS.PRIVATE_KEY:
        return showPrivateKey;
      case CREDENTIALED_KEYS.SECRET:
        return showSecret;
      default:
        break;
    }

    return null;
  };

  const isMaskedField = (key, isTypePassword) => {
    const isPassword = key === CREDENTIALED_KEYS.PASSWORD && !showPassword;

    const isPrivateKey =
      key === CREDENTIALED_KEYS.PRIVATE_KEY && !showPrivateKey;

    const isSecret = key === CREDENTIALED_KEYS.SECRET && !showSecret;

    return (
      (isTypePassword && isPassword) ||
      (isTypePassword && isPrivateKey) ||
      (isTypePassword && isSecret)
    );
  };

  useEffect(() => {
    let passwordTimeout;

    if (showPassword) {
      passwordTimeout = setTimeout(() => {
        setShowPassword(false);
      }, 5000);
    }

    return () => {
      clearTimeout(passwordTimeout);
    };
  }, [showPassword]);

  useEffect(() => {
    let privateKeyTimeout;

    if (showPrivateKey) {
      privateKeyTimeout = setTimeout(() => {
        setShowPrivateKey(false);
      }, 5000);
    }

    return () => {
      clearTimeout(privateKeyTimeout);
    };
  }, [showPrivateKey]);

  useEffect(() => {
    let secretTimeout;

    if (showSecret) {
      secretTimeout = setTimeout(() => {
        setShowSecret(false);
      }, 5000);
    }

    return () => {
      clearTimeout(secretTimeout);
    };
  }, [showSecret]);

  useEffect(() => {
    let sshTokenTimeout;

    if (isSshTokenVisible) {
      sshTokenTimeout = setTimeout(() => {
        setIsSshTokenVisible(false);
      }, 5000);
    }

    return () => {
      clearTimeout(sshTokenTimeout);
    };
  }, [isSshTokenVisible]);

  // Reset SSH token when session data changes
  useEffect(() => {
    setSshToken('');
    setSshTokenExpiry();
    setIsError(false);
    setIsSshTokenVisible(false);
  }, [session?.sessionKey]);

  const handleGenerateTokenOnClick = () => {
    setIsLoading(true);
    setIsError(false);

    generateSshTokenApi(session?.sessionKey)
      .then((response) => {
        const { sshCommand, expiresAt } = response;

        if (sshCommand && expiresAt) {
          setSshToken(sshCommand);
          setSshTokenExpiry(expiresAt);
        } else {
          setIsError(true);
        }
      })
      .catch((e) => {
        setIsError(true);
        Logger.error(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <section className={classes} data-testid={dataTestId}>
      <TabGroup
        className='SpvActiveSession-tabGroup'
        value={activeTab}
        isVerticalWithOverflow={isMobileWidth}
        onChange={handleChange}>
        <TabItem
          label={localize(msgs.pam.sidePanel.session)}
          value='1'
          icon={
            <Icon kind='PrivilegedSession' size='medium' color='neutral-1000' />
          }
        />
        {!userAccessData?.roleKey && (
          <TabItem
            label={localize(msgs.pam.sidePanel.asset)}
            value='2'
            icon={
              <Icon
                kind='AssetTargetSystem'
                size='medium'
                color='neutral-1000'
              />
            }
          />
        )}
        {hasRole && (
          <TabItem
            label={
              isJitForApps
                ? localize(msgs.pam.sidePanel.asset)
                : localize(msgs.pam.sidePanel.role)
            }
            value='3'
            icon={
              isJitForApps ? (
                <Icon
                  kind='AssetTargetSystem'
                  size='medium'
                  color='neutral-1000'
                />
              ) : (
                <Icon kind='Role' size='medium' color='neutral-1000' />
              )
            }
          />
        )}
        {hasEntitlements && (
          <TabItem
            label={localize(msgs.pam.sidePanel.entitlements)}
            value='4'
            icon={
              <Icon kind='Entitlement' size='medium' color='neutral-1000' />
            }
          />
        )}
      </TabGroup>
      <TabContent
        className='SpvActiveSession-tabContent'
        value={activeTab}
        tabValue='1'>
        {sessionDetails &&
          (sessionDetails.error ? (
            <section className='SpvSection SpvActiveSession-tabSection SpvActiveSession-tabSection--isCredentialsError'>
              <div className='SpvActiveSession-tabSection-error'>
                <Icon
                  kind='AlertCriticalFilled'
                  color='--color-foreground-critical-medium'
                />{' '}
                <P
                  variant={3}
                  color='--color-foreground-critical-bold'
                  className='SpvActiveSession-tabSection-error-message'>
                  {localize(msgs.pam.sidePanel.mfaUnsuccessful)}
                  <Button
                    onClick={sessionDetails.error}
                    isEnabled
                    kind='ghost'
                    rightIcon={<Icon kind='ArrowRight' size='medium' />}
                    color='--color-foreground-critical-bold'
                    size='small'>
                    {localize(msgs.pam.sidePanel.tryAgain)}
                  </Button>
                </P>
              </div>
            </section>
          ) : (
            <SpvSection className='SpvActiveSession-credentials'>
              {Object.entries(sessionDetails).map(([key, valueObject]) => {
                const translationKey = `credentialledField${key}`;
                const isCopyButtonClicked = clickedCopyButton === key;

                msgs[translationKey] = {
                  id: `consoleCredential.${key}`,
                  defaultMessage: camelCaseToTitleCase(key),
                };

                const field = localize(msgs[translationKey]);
                const { value } = valueObject;
                const { effect } = valueObject;

                const isTypePassword =
                  effect === 'PASSWORD' || effect === 'TEXTAREAPASSWORD';

                return (
                  <section className='SpvActiveSession-credential'>
                    <Box className='SpvActiveSession-credential-field'>
                      <InputField
                        label={
                          <Typography
                            kind='h4'
                            className='SpvActiveSession-credential-label'>
                            {field}
                          </Typography>
                        }
                        type={
                          isMaskedField(key, isTypePassword)
                            ? 'password'
                            : 'text'
                        }
                        placeholder={field}
                        name={field.toLowerCase()}
                        suffixButtonType={isTypePassword ? 'password' : null}
                        value={value}
                        suffixOnClick={() => {
                          handleMaskedFields(key);
                        }}
                        suffixIsToggle={showToggleState(key)}
                        setValue={() => {}}
                        isReadOnly
                      />
                    </Box>
                    <CopyButtonWithTooltip
                      handleCopyOnClick={handleCopyOnClick}
                      isCopyButtonClicked={isCopyButtonClicked}
                      copyButtonKey={key}
                      copyButtonValue={value}
                    />
                  </section>
                );
              })}
            </SpvSection>
          ))}
        {isCredentialless &&
          (isUnix || isAdServer) &&
          isTokenGenerationEnabled && (
            <Box
              className={`SpvActiveSession-credentials ${
                isCredentiallessWithPasswordEnabled
                  ? 'SpvActiveSession-credentials--enabled'
                  : ''
              }`}>
              <section className='SpvActiveSession-sshToken'>
                <Box className='SpvActiveSession-credential-field'>
                  {sshToken ? (
                    <InputField
                      kind='singleline'
                      label={
                        <Typography
                          className='SpvActiveSession-credential-label'
                          kind='h4'>
                          {localize(msgs.pam.sidePanel.sshCommand)}
                        </Typography>
                      }
                      suffixButtonType='password'
                      isResetButtonVisible={false}
                      type={isSshTokenVisible ? 'text' : 'password'}
                      suffixOnClick={() => {
                        setIsSshTokenVisible(!isSshTokenVisible);
                      }}
                      value={sshToken}
                    />
                  ) : (
                    <>
                      <Typography
                        className='SpvActiveSession-credential-label'
                        kind='h4'>
                        {localize(msgs.pam.sidePanel.sshCommand)}
                      </Typography>
                      <Box
                        className={`SpvActiveSession-token-container ${
                          isError
                            ? 'SpvActiveSession-token-container--error'
                            : ''
                        }`}>
                        <Button
                          dataTheme='light'
                          className='SpvActiveSession-token-button'
                          kind='subtle'
                          size='small'
                          onClick={handleGenerateTokenOnClick}
                          isLoading={isLoading}>
                          {localize(msgs.pam.sidePanel.generate)}
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
                <CopyButtonWithTooltip
                  key={session?.sessionKey}
                  handleCopyOnClick={handleCopyOnClick}
                  copyButtonKey='sshToken'
                  copyButtonValue={sshToken}
                  isCopyButtonDisabled={!sshToken}
                />
              </section>
              {sshTokenExpiry && (
                <Box className='SpvActiveSession-sshToken-countdownTimer'>
                  <Icon
                    size='xxSmall'
                    kind='Time'
                    color='--color-foreground-info-medium'
                  />
                  <Typography
                    color='--color-foreground-info-medium'
                    kind='body3-bold'>
                    <CountdownTimer
                      targetDate={sshTokenExpiry}
                      leftText={
                        <Typography
                          kind='body3'
                          color='--color-foreground-info-medium'>
                          {localize(msgs.pam.sidePanel.left)}
                        </Typography>
                      }
                      onTimerExpired={() => {
                        setSshToken('');
                        setSshTokenExpiry();
                      }}
                      secondsAfterMinutes='5'
                    />
                  </Typography>
                </Box>
              )}
              {isError && (
                <InlineMessage
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                  text={localize(msgs.pam.sidePanel.generateFailure)}
                  kind='critical'
                  size='small'
                  className='SpvActiveSession-sshToken-errorMessage'
                />
              )}
            </Box>
          )}
        {hasSessionTimeFields && (
          <SpvSection
            className={sessionDetails && 'SpvActiveSession-tabSection'}
            title={localize(msgs.pam.sidePanel.time)}
            fields={[
              {
                label: localize(msgs.pam.sidePanel.startTime),
                value: formatLocalizedDateTime(session?.sessionStartDate),
              },
              {
                label: localize(msgs.pam.sidePanel.endTime),
                value: formatLocalizedDateTime(session?.sessionEndDate),
              },
            ]}
          />
        )}
        {sapTransactionCodesHasFields && (
          <SpvSection
            className={hasSessionTimeFields && 'SpvActiveSession-tabSection'}
            title={localize(msgs.pam.sidePanel.sapTransactionCodes)}
            fields={[
              {
                label: localize(msgs.pam.sidePanel.tCodesSelected),
                value: tCodes.join(', '),
              },
            ]}
          />
        )}
        {isCredentialless && hasTargetSystemFields && (
          <SpvSection
            className={
              credentiallessSectionHasDivider && 'SpvActiveSession-tabSection'
            }
            title={localize(msgs.pam.sidePanel.targetSystem)}
            fields={[
              {
                label: localize(msgs.pam.sidePanel.targetServer),
                value: endpointDetails?.customproperty9,
              },
              {
                label: localize(msgs.pam.sidePanel.portNumber),
                value: endpointDetails?.customproperty35,
              },
            ]}
          />
        )}
        {showRemoteAppName && (
          <SpvSection
            className={
              remoteAppSectionHasDivider && 'SpvActiveSession-tabSection'
            }
            title={localize(msgs.pam.sidePanel.remoteApplication)}
            fields={[
              {
                label: localize(msgs.pam.sidePanel.name),
                value: remoteAppName,
              },
            ]}
          />
        )}
        {hasSessionAccountFields && !hasRole && (
          <SpvSection
            className={
              accountSectionHasDivider && 'SpvActiveSession-tabSection'
            }
            title={localize(msgs.pam.sidePanel.account)}
            fields={[
              {
                label: localize(msgs.pam.sidePanel.name),
                value: session?.accountName,
              },
              {
                label: localize(msgs.pam.sidePanel.platform),
                value: accountDetails?.platformType,
              },
              {
                label: localize(msgs.pam.sidePanel.description),
                value:
                  accountDetails?.description ||
                  userAccessData?.accountDescription,
              },
              {
                label: localize(msgs.pam.sidePanel.accountType),
                value: accountDetails?.pamAccountType,
              },
              {
                label: localize(msgs.pam.sidePanel.authenticationMechanism),
                value: accountDetails?.accountConfig?.authenticationType,
              },
            ]}
          />
        )}
      </TabContent>
      {!userAccessData?.roleKey && (
        <TabContent
          className='SpvActiveSession-assetTab'
          value={activeTab}
          tabValue='2'>
          <SpvEndpoint data={endpointDetails} />
        </TabContent>
      )}
      {hasRole && (
        <TabContent
          className='SpvActiveSession-tabContent'
          value={activeTab}
          tabValue='3'>
          <SpvRole data={userAccessData} roleDetails={roleDetails} />
        </TabContent>
      )}
      {hasEntitlements && (
        <TabContent
          className='SpvActiveSession-tabContent'
          value={activeTab}
          tabValue='4'>
          <SpvEntitlements
            data={userAccessData}
            initialEntitlements={entitlements}
          />
        </TabContent>
      )}
    </section>
  );
}

SpvActiveSession.propTypes = {
  userAccessData: ModuleDetails,
  accountDetails: ModuleDetails,
  endpointDetails: ModuleDetails,
  session: Session,
  sessionDetails: SessionDetails,
  roleDetails: RoleDetails,
  entitlements: PropTypes.arrayOf(Entitlement),
  isMobileWidth: PropTypes.bool.isRequired,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  isJitForApps: PropTypes.bool,
};

SpvActiveSession.defaultProps = {
  userAccessData: null,
  accountDetails: null,
  endpointDetails: null,
  session: null,
  sessionDetails: null,
  roleDetails: null,
  entitlements: null,
  dataTestId: null,
  className: null,
  isJitForApps: false,
};

CopyButtonWithTooltip.propTypes = {
  handleCopyOnClick: PropTypes.func,
  isCopyButtonClicked: PropTypes.bool,
  isCopyButtonDisabled: PropTypes.bool,
  copyButtonKey: PropTypes.string,
  copyButtonValue: PropTypes.string,
};

CopyButtonWithTooltip.defaultProps = {
  handleCopyOnClick: null,
  isCopyButtonClicked: null,
  copyButtonKey: null,
  copyButtonValue: null,
  isCopyButtonDisabled: false,
};

export default SpvActiveSession;
